import type { MessageKey } from '@freightapi/i18n'
import { Button, DataTable, type DataTableColumn, formatDateTime } from '@freightapi/ui'
import { createFileRoute } from '@tanstack/react-router'
import { type ReactNode, useCallback, useState } from 'react'
import { ResourceView } from '../components/ResourceView'
import {
  asApiError,
  type Conformance,
  type ConformanceScenarioStatus,
  docsUrl,
  evaluateConformance,
  fetchConformance,
  fetchOrganization,
  type OrganizationSettings,
  requestLiveAccess,
} from '../lib/api'
import { useConsoleI18n } from '../lib/locale'
import { useResource } from '../lib/resource'
import { useCan, useCurrentSession } from '../lib/session'

/**
 * The conformance checklist (M3-18, ADR 0005): the twelve things an organisation has to have shown
 * in the sandbox before Cargofollow will consider turning live keys on.
 *
 * The screen is deliberately not a form. Nothing here is ticked off by pressing something: the
 * evaluator derives every row from evidence the organisation's own traffic left behind — events,
 * request logs, webhook deliveries — so "check now" re-reads and never records. That is also why an
 * open scenario carries a link instead of a button: what a developer needs is the page that says
 * how to demonstrate it, not a way to mark it done.
 *
 * `ready_for_live` unlocks the request, not live itself. Releasing an organisation stays a human
 * act at Cargofollow (ADR 0005), and the mail this button sends is the same one M3-03 sends from the
 * API-keys screen.
 *
 * No mode switch. The checklist is about test mode by definition — it is what you show *before*
 * live keys exist — so the endpoints ignore `freightapi-mode` and this screen never sends one.
 */
export const Route = createFileRoute('/_app/conformance')({ component: ConformanceChecklist })

interface ChecklistData {
  readonly conformance: Conformance
  readonly organization: OrganizationSettings
}

/**
 * The explanation per scenario, by name. The API sends stable scenario names and a documentation
 * path, never prose: the console owns the words, in three languages (ADR 0010). A name this build
 * does not know — a scenario added by a newer API — is shown by its name rather than hidden, so a
 * checklist is never silently one item short.
 */
const SCENARIO_TEXT = {
  create_shipment: [
    'conformance.scenario.create_shipment.title',
    'conformance.scenario.create_shipment.body',
  ],
  validate_with_error_handled: [
    'conformance.scenario.validate_with_error_handled.title',
    'conformance.scenario.validate_with_error_handled.body',
  ],
  issue: ['conformance.scenario.issue.title', 'conformance.scenario.issue.body'],
  signature_via_link: [
    'conformance.scenario.signature_via_link.title',
    'conformance.scenario.signature_via_link.body',
  ],
  delivery_with_pod: [
    'conformance.scenario.delivery_with_pod.title',
    'conformance.scenario.delivery_with_pod.body',
  ],
  webhook_endpoint_configured: [
    'conformance.scenario.webhook_endpoint_configured.title',
    'conformance.scenario.webhook_endpoint_configured.body',
  ],
  webhook_echo_verified: [
    'conformance.scenario.webhook_echo_verified.title',
    'conformance.scenario.webhook_echo_verified.body',
  ],
  webhook_delivery_succeeded: [
    'conformance.scenario.webhook_delivery_succeeded.title',
    'conformance.scenario.webhook_delivery_succeeded.body',
  ],
  idempotency_key_used: [
    'conformance.scenario.idempotency_key_used.title',
    'conformance.scenario.idempotency_key_used.body',
  ],
  integrity_checked: [
    'conformance.scenario.integrity_checked.title',
    'conformance.scenario.integrity_checked.body',
  ],
  pdf_downloaded: [
    'conformance.scenario.pdf_downloaded.title',
    'conformance.scenario.pdf_downloaded.body',
  ],
  auto_sandbox_run: [
    'conformance.scenario.auto_sandbox_run.title',
    'conformance.scenario.auto_sandbox_run.body',
  ],
} as const satisfies Record<string, readonly [MessageKey<'console'>, MessageKey<'console'>]>

type KnownScenario = keyof typeof SCENARIO_TEXT

function isKnown(scenario: string): scenario is KnownScenario {
  return Object.hasOwn(SCENARIO_TEXT, scenario)
}

function ConformanceChecklist() {
  const { t, locale } = useConsoleI18n()
  const can = useCan()
  const session = useCurrentSession()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [notice, setNotice] = useState<string | undefined>(undefined)

  const load = useCallback(async (signal: AbortSignal): Promise<ChecklistData> => {
    const [checklist, organization] = await Promise.all([
      fetchConformance(signal),
      fetchOrganization(signal),
    ])
    return { conformance: checklist.conformance, organization: organization.organization }
  }, [])
  const { state, reload } = useResource(load)

  const run = useCallback(
    async (action: () => Promise<unknown>) => {
      setBusy(true)
      setError(undefined)
      try {
        await action()
        reload()
      } catch (cause) {
        const failure = asApiError(cause)
        setError(failure.problem?.detail ?? t(failure.messageKey))
      } finally {
        setBusy(false)
      }
    },
    [reload, t],
  )

  const columns: readonly DataTableColumn<ConformanceScenarioStatus>[] = [
    {
      key: 'status',
      header: t('conformance.column.status'),
      cell: (row) => <ScenarioStatus status={row.status} />,
    },
    {
      key: 'scenario',
      header: t('conformance.column.scenario'),
      cell: (row) => <ScenarioText entry={row} />,
    },
    {
      key: 'moment',
      header: t('conformance.column.moment'),
      cell: (row) =>
        row.passed_at === null ? (
          <span className="text-muted">—</span>
        ) : (
          <span className="text-ink">{formatDateTime(row.passed_at, locale)}</span>
        ),
    },
  ]

  return (
    <section className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-semibold text-display text-ink">{t('nav.conformance.label')}</h1>
        <Button variant="ghost" disabled={busy} onClick={() => void run(evaluateConformance)}>
          {t('conformance.recheck')}
        </Button>
      </div>
      <p className="text-body text-muted">{t('conformance.intro')}</p>

      {error === undefined ? null : (
        <p role="alert" className="text-body text-danger">
          {error}
        </p>
      )}
      {notice === undefined ? null : (
        <p role="status" className="text-body text-muted">
          {notice}
        </p>
      )}

      <ResourceView state={state}>
        {({ conformance, organization }) => (
          <div className="flex flex-col gap-6">
            <Progress conformance={conformance} />
            {organization.live_enabled ? (
              <Panel title={t('conformance.enabled.title')} body={t('conformance.enabled.body')} />
            ) : (
              <Panel
                title={
                  conformance.ready_for_live
                    ? t('conformance.ready.title')
                    : t('conformance.blocked.title')
                }
                body={
                  conformance.ready_for_live
                    ? t('conformance.ready.body')
                    : t('conformance.blocked')
                }
              >
                {organization.live_requested_at === null ? null : (
                  <p className="text-body text-muted">
                    {t('conformance.requested', {
                      moment: formatDateTime(organization.live_requested_at, locale),
                    })}
                  </p>
                )}
                {can('organization:update') ? (
                  <div>
                    <Button
                      disabled={!conformance.ready_for_live || busy}
                      onClick={() =>
                        void run(async () => {
                          await requestLiveAccess()
                          setNotice(t('live.request.sent', { email: session.user.email }))
                        })
                      }
                    >
                      {t('live.request')}
                    </Button>
                  </div>
                ) : null}
              </Panel>
            )}
            <DataTable
              caption={t('conformance.table.caption')}
              columns={columns}
              rows={conformance.scenarios}
              rowKey={(row) => row.scenario}
              empty={null}
            />
          </div>
        )}
      </ResourceView>
    </section>
  )
}

/** How far the organisation is, as a number and as a bar a glance can read. */
function Progress({ conformance }: { readonly conformance: Conformance }) {
  const { t, locale } = useConsoleI18n()
  const percentage = conformance.total === 0 ? 0 : (conformance.passed / conformance.total) * 100

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-semibold text-ink text-lead">
          {t('conformance.progress', {
            passed: String(conformance.passed),
            total: String(conformance.total),
          })}
        </p>
        <p className="text-caption text-muted">
          {conformance.evaluated_at === null
            ? t('conformance.never_checked')
            : t('conformance.checked', {
                moment: formatDateTime(conformance.evaluated_at, locale),
              })}
        </p>
      </div>
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={conformance.total}
        aria-valuenow={conformance.passed}
        aria-label={t('conformance.table.caption')}
        className="h-2 w-full overflow-hidden rounded-full bg-line"
      >
        <div className="h-full rounded-full bg-brand-700" style={{ width: `${percentage}%` }} />
      </div>
    </div>
  )
}

function Panel({
  title,
  body,
  children,
}: {
  readonly title: string
  readonly body: string
  readonly children?: ReactNode
}) {
  return (
    <section className="flex flex-col gap-3 rounded-card border border-line p-4">
      <h2 className="font-semibold text-ink text-lead">{title}</h2>
      <p className="text-body text-muted">{body}</p>
      {children}
    </section>
  )
}

const STATUS_LABEL = {
  passed: 'conformance.status.passed',
  pending: 'conformance.status.pending',
  failed: 'conformance.status.failed',
} as const satisfies Record<ConformanceScenarioStatus['status'], MessageKey<'console'>>

/** The tick, with the word next to it: colour alone is not a status (WCAG 1.4.1). */
function ScenarioStatus({ status }: { readonly status: ConformanceScenarioStatus['status'] }) {
  const { t } = useConsoleI18n()
  const label = t(STATUS_LABEL[status])

  return (
    <span
      className={
        status === 'passed'
          ? 'flex items-baseline gap-2 text-ink'
          : 'flex items-baseline gap-2 text-muted'
      }
    >
      <span aria-hidden="true">{status === 'passed' ? '✓' : '○'}</span>
      <span>{label}</span>
    </span>
  )
}

/** The name of a scenario, what it proves, and — while it is open — where that is explained. */
function ScenarioText({ entry }: { readonly entry: ConformanceScenarioStatus }) {
  const { t } = useConsoleI18n()
  if (!isKnown(entry.scenario)) {
    return <span className="font-mono text-caption text-ink">{entry.scenario}</span>
  }
  const [title, body] = SCENARIO_TEXT[entry.scenario]

  return (
    <span className="flex flex-col gap-1">
      <span className="text-ink">{t(title)}</span>
      <span className="text-caption text-muted">{t(body)}</span>
      {entry.hint === null ? null : (
        <a
          href={docsUrl(entry.hint)}
          target="_blank"
          rel="noreferrer"
          className="text-brand-700 text-caption underline"
        >
          {t('conformance.hint')}
        </a>
      )}
    </span>
  )
}
